import { ExpandMore as ExpandIcon, Search } from "@mui/icons-material";
import {
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import React, { ReactNode, useState } from "react";

import classNames from "classnames";
import { MuiBox, MuiInfoToolTip, MuiTextField } from "..";
import { getHelperText, removeWhiteSpace } from "../../helpers/utils";
import { COLORS } from "../../styles/constants.style";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiTypography } from "../typography/MuiTypography";
import {
  MuiSelectWrapper,
  searchWithInputStyle,
  SelectTextboxWrapper,
} from "./MuiSelect.style";

export interface OptionsDataType {
  value: string | number;
  label: string;
  [key: string]: any;
}

export interface OptionsDataTypeWithNode {
  value: string | number;
  label: ReactNode;
  [key: string]: any;
}
export interface CustomMenu {
  title: string;
  data: CustomMenuValue[];
}
export interface CustomMenuValue {
  name: string;
  value: string;
}

type MuiSelectFieldProps = SelectProps & {
  label: string;
  options: OptionsDataType[] | OptionsDataTypeWithNode[];
  className?: string;
  helperText?: string;
  isMandatory?: boolean;
  visibleDefaultSelectOption?: boolean;
  isErrorIconEnabled?: boolean;
  showToolTip?: boolean;
  onToggleToolTip?: (isVisible: boolean) => void;
  infoMessage?: string;
  isInfoIconVisible?: boolean;
  tooltipChildren?: ReactNode;
  disabledItems?: (string | number)[];
  showSearch?: boolean;
  searchPlaceholder?: string;
  isHorizontalLabel?: boolean;
  readOnly?: boolean;
  getSubText?: (option: OptionsDataType | OptionsDataTypeWithNode) => string; // Accessor function for subText
  isOptionTitleInBold?: boolean; // Optional prop to make option title bold
  shouldDisableInfoIcon?: boolean; // Optional prop to disable info icon
  requiredARIALabel: string;
};

export const MuiSelect: React.FC<MuiSelectFieldProps> = ({
  label,
  options = [],
  className = "",
  helperText,
  isMandatory,
  disabled,
  visibleDefaultSelectOption,
  placeholder,
  isErrorIconEnabled = true,
  showToolTip,
  onToggleToolTip,
  infoMessage,
  isInfoIconVisible,
  tooltipChildren,
  id,
  disabledItems = [],
  showSearch = false,
  searchPlaceholder = "Search",
  isHorizontalLabel = false,
  readOnly = false,
  getSubText,
  isOptionTitleInBold = false,
  shouldDisableInfoIcon,
  requiredARIALabel = "Required",
  ...rest
}) => {
  const defaultPlaceholder = placeholder;
  // Function to get the label from the selected value
  const getLabelFromValue = (value: string | number | undefined) => {
    if (!value) return defaultPlaceholder; // Fallback text when no value is selected
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : defaultPlaceholder; // Return label if found
  };

  const [filterText, setFilterText] = useState("");
  const [menuWidth, setMenuWidth] = useState<string | number>("auto");

  const handleSearchFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilterText(event.target.value);
  };

  // Prevent default selection of an option when typing
  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation(); // Stops default selection behavior
  };

  // Filter options based on search input and if search is disabled, show all options
  const filteredOptions = showSearch
    ? options.filter((option) => {
        const labelMatch =
          typeof option.label === "string" &&
          option.label.toLowerCase().includes(filterText.toLowerCase());
        const subTextMatch = getSubText?.(option)
          ?.toLowerCase()
          .includes(filterText.toLowerCase());
        return labelMatch || subTextMatch;
      })
    : options;

  const handleOpen = (event: React.SyntheticEvent<Element, Event>) => {
    // Get the width of the Select component dynamically
    const selectBox = event.currentTarget.getBoundingClientRect();
    setMenuWidth(selectBox.width);
    setFilterText("");
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  // Merge incoming inputProps with internal ones, preserving data-testid based on `id`
  const mergedInputProps: SelectProps["inputProps"] = {
    "data-testid": id,
    readOnly,
    "aria-label": `${label} ${isMandatory && !disabled ? requiredARIALabel : defaultPlaceholder}`,
    ...rest.inputProps,
  };

  return (
    <MuiSelectWrapper
      className={classNames(className, {
        "horizontal-wrapper": isHorizontalLabel,
      })}
    >
      {label && (
        <MuiBox className={"title"}>
          <MuiTypography
            className={classNames("mui-label", {
              disabled: disabled,
              "horizontal-label": isHorizontalLabel,
            })}
            component="label"
            variant="h3"
          >
            {label}{" "}
            {isMandatory && !disabled && (
              <label className="asterisk" aria-label={requiredARIALabel}>
                *
              </label>
            )}
          </MuiTypography>
          {infoMessage && isInfoIconVisible && (
            <MuiBox className="tooltip-container">
              <MuiInfoToolTip
                message={infoMessage}
                placement="top"
                className="tooltip-wrapper"
                shouldDisableInfoIcon={shouldDisableInfoIcon}
                tooltipChildren={tooltipChildren}
              />
            </MuiBox>
          )}
        </MuiBox>
      )}
      <Select
        displayEmpty
        disabled={disabled}
        onOpen={showSearch ? handleOpen : undefined}
        MenuProps={{
          disableAutoFocusItem: showSearch,
          PaperProps: {
            sx: showSearch
              ? {
                  width: menuWidth,
                  ...searchWithInputStyle,
                }
              : {},
          },
        }}
        renderValue={(value) => {
          const stringValue = value as string;
          if (!stringValue) {
            return (
              <MuiTypography
                variant="body3SemiBold"
                color={COLORS.cirrus_black5}
              >
                {defaultPlaceholder}
              </MuiTypography>
            );
          }
          return getLabelFromValue(stringValue);
        }}
        IconComponent={ExpandIcon}
        {...rest}
        inputProps={mergedInputProps}
        className="mui-select"
      >
        {showSearch && (
          <SelectTextboxWrapper>
            <MuiTextField
              placeholder={searchPlaceholder}
              className="mui-text-field select-search-input"
              value={filterText}
              onChange={handleSearchFilterChange}
              onKeyDown={handleSearchKeyDown}
              onClick={handleInputClick}
              showCloseIcon
              id="select-box-search"
              startAdornment={
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              }
              requiredARIALabel={requiredARIALabel}
            />
          </SelectTextboxWrapper>
        )}
        {visibleDefaultSelectOption && (
          <MenuItem key="Select" value="">
            {defaultPlaceholder}
          </MenuItem>
        )}
        {filteredOptions.map((option, posValue) => (
          <MenuItem
            key={`${posValue}-${option.value}`}
            value={option.value}
            disabled={disabledItems.includes(option.value)}
          >
            <MuiBox className="option-container">
              <MuiTypography
                className={isOptionTitleInBold ? "bold-option-title" : ""}
              >
                {option.label}
              </MuiTypography>
              {getSubText && (
                <MuiTypography variant="body3">
                  {getSubText(option)}
                </MuiTypography>
              )}
            </MuiBox>
          </MenuItem>
        ))}
      </Select>
      {rest.error && (
        <FormHelperText error={rest.error}>
          {helperText && (
            <MuiBox
              role="alert"
              aria-description={getHelperText(helperText, label)}
              className="error-icon"
            >
              {isErrorIconEnabled && <ErrorCrossIcon />}
              <div aria-hidden id={`helper-text-${removeWhiteSpace(label)}`}>
                {getHelperText(helperText, label)}
              </div>
            </MuiBox>
          )}
        </FormHelperText>
      )}
    </MuiSelectWrapper>
  );
};
